/* eslint-disable @typescript-eslint/no-unsafe-member-access */
import { LoadState } from '@inyova/models';

import { Action, createReducer, on } from '@ngrx/store';
import * as AccountActions from '@account/account.actions';
import * as HomeActions from '@home/home.actions';

import { isExtendedPerformanceRange } from '@home/components/home-performance-chart/home-performance-chart.util';
import { SavingsPlanSummary } from '@shared/models/Account';
import {
  BalanceSheet,
  CompaniesStockPerformanceAccount,
  CrowdInvestingValues,
  EndValuesAccount,
  PerformanceAccount,
  PerformanceData,
  PerformanceDataFilter,
  PerformanceFilterRange,
  PerformanceMarketUpdates,
  PerformanceSummary,
  PerformanceType
} from '@shared/models/Home';

import { addOrUpdateAccount } from '../home.utils';

export interface PerformanceState {
  accountsGraphData: PerformanceAccount[];
  accountsEndValues: EndValuesAccount[];
  companiesStockPerformance: null | CompaniesStockPerformanceAccount[];
  crowdInvestorData: CrowdInvestingValues;
  savingsPlanSummary: SavingsPlanSummary | null;
  performanceData: PerformanceData;
  performanceSummary: PerformanceSummary | null;
  balanceSheet: BalanceSheet | null;
  marketUpdates: PerformanceMarketUpdates;
}

export const initialState: PerformanceState = {
  accountsGraphData: [],
  accountsEndValues: [],
  companiesStockPerformance: [],
  crowdInvestorData: {
    number_of_shares: 0,
    total_stock_value: 0,
    stock_value: 0
  },
  savingsPlanSummary: null,
  performanceData: {
    graphData: [],
    loading: false,
    filter: {
      range: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE,
      chartType: PerformanceType.PERFORMANCE,
      showPortfolioChanges: true,
      showBenchmark: false,
      lastExtendedRange: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE
    }
  },
  performanceSummary: null,
  balanceSheet: null,
  marketUpdates: {
    marketUpdate: null,
    loadState: 'INIT'
  }
};

const performanceReducer = createReducer(
  initialState,
  on(HomeActions.getEndValuesSuccess, (state, action) => ({
    ...state,
    endValues: action.data
  })),
  on(HomeActions.getCompaniesStockPerformanceSuccess, (state, action) => ({
    ...state,
    companiesStockPerformance: addOrUpdateAccount(
      { account: action.account, initialised: true, companies: action.data, hasError: false },
      state.companiesStockPerformance
    ) as CompaniesStockPerformanceAccount[]
  })),
  on(HomeActions.getCompaniesStockPerformanceFail, (state, action) => ({
    ...state,
    companiesStockPerformance: addOrUpdateAccount(
      { account: action.account, initialised: true, companies: [], hasError: true },
      state.companiesStockPerformance
    ) as CompaniesStockPerformanceAccount[]
  })),
  on(HomeActions.getGraphData, (state, action) => ({
    ...state,
    accountsGraphData: addOrUpdateAccount(
      {
        account: action.account,
        initialised: false,
        graphData: [],
        hasError: false
      },
      state.accountsGraphData
    ) as PerformanceAccount[]
  })),
  on(HomeActions.getGraphDataSuccess, (state, action) => ({
    ...state,
    accountsGraphData: addOrUpdateAccount(
      {
        account: action.account,
        initialised: true,
        graphData: action.graphData,
        hasError: false
      },
      state.accountsGraphData
    ) as PerformanceAccount[]
  })),
  on(HomeActions.getGraphDataFail, (state, action) => ({
    ...state,
    error: action.error,
    accountsGraphData: addOrUpdateAccount(
      { account: action.account, initialised: true, graphData: null, hasError: true },
      state.accountsGraphData
    ) as PerformanceAccount[]
  })),
  on(HomeActions.loadPerformanceFilters, (state) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      loading: true
    }
  })),
  on(HomeActions.hydratePerformanceFilter, (state, { range, chartType, showPortfolioChanges, showBenchmark, lastExtendedRange }) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      filter: {
        range,
        chartType,
        showPortfolioChanges,
        showBenchmark,
        lastExtendedRange
      }
    }
  })),
  on(HomeActions.getPerformanceGraph, (state, { range }) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      loading: range !== PerformanceFilterRange.ALL,
      ...(range === PerformanceFilterRange.ALL && {
        filter: { ...state.performanceData.filter, range, lastExtendedRange: range }
      })
    }
  })),
  on(HomeActions.getPerformanceGraphSuccess, (state, action) => ({
    ...state,
    performanceData: {
      graphData: [...action.graphData],
      loading: false,
      filter: {
        ...state.performanceData.filter,
        range: action.range,
        ...(isExtendedPerformanceRange(action.range) && {
          lastExtendedRange: action.range
        })
      }
    }
  })),
  on(HomeActions.getPerformanceGraphFailed, (state) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      loading: false
    }
  })),
  on(HomeActions.togglePerformanceGraphType, (state, { chartType }) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      filter: {
        ...state.performanceData.filter,
        chartType
      }
    }
  })),
  on(HomeActions.togglePerformanceEvents, (state, { showPortfolioChanges }) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      filter: {
        ...state.performanceData.filter,
        showPortfolioChanges
      }
    }
  })),
  on(HomeActions.togglePerformanceBenchmarkType, (state, { showBenchmark }) => ({
    ...state,
    performanceData: {
      ...state.performanceData,
      filter: {
        ...state.performanceData.filter,
        showBenchmark
      }
    }
  })),
  on(HomeActions.getEndValues, (state, action) => ({
    ...state,
    accountsEndValues: addOrUpdateAccount(
      {
        account: action.account,
        initialised: false,
        endValues: null,
        hasError: false
      },
      state.accountsEndValues
    ) as EndValuesAccount[]
  })),
  on(HomeActions.getEndValuesSuccess, (state, action) => ({
    ...state,
    accountsEndValues: addOrUpdateAccount(
      {
        account: action.account,
        initialised: true,
        endValues: action.data,
        hasError: false
      },
      state.accountsEndValues
    ) as EndValuesAccount[]
  })),
  on(HomeActions.getCrowdInvestingDataSuccess, (state, action) => ({
    ...state,
    crowdInvestorData: action.data
  })),
  on(HomeActions.getSavingsPlanSummarySuccess, (state, action) => ({
    ...state,
    savingsPlanSummary: action.data
  })),
  on(HomeActions.getSavingsPlanSummaryFail, (state, action) => ({
    ...state,
    savingsPlanSummary: action.data
  })),
  on(HomeActions.getPerformanceSummarySuccess, (state, action) => ({
    ...state,
    performanceSummary: action.data
  })),
  on(HomeActions.getPerformanceSummaryFail, (state) => ({
    ...state,
    performanceSummary: null
  })),
  on(HomeActions.getBalanceSheetSuccess, (state, action) => ({
    ...state,
    balanceSheet: action.data
  })),
  on(HomeActions.getBalanceSheetFail, (state) => ({
    ...state,
    balanceSheet: null
  })),
  on(HomeActions.getPerformanceMarketUpdate, (state) => {
    return state.marketUpdates.loadState === 'LOADED'
      ? state
      : ({ ...state, marketUpdates: { ...state.marketUpdates, loadState: 'LOADING' } } as PerformanceState);
  }),
  on(HomeActions.getPerformanceMarketUpdateSuccess, (state, { marketUpdate }) => ({
    ...state,
    marketUpdates: {
      marketUpdate,
      loadState: 'LOADED' as const
    }
  })),
  on(HomeActions.getPerformanceMarketUpdateFail, (state) => ({
    ...state,
    marketUpdates: {
      marketUpdate: null,
      loadState: { error: 'Something went wrong' } as const
    }
  })),
  on(HomeActions.dismissMarketUpdateSuccess, (state) => ({
    ...state,
    marketUpdates: {
      ...state.marketUpdates,
      marketUpdate: {
        ...state.marketUpdates.marketUpdate,
        dismissed: true
      }
    }
  })),
  on(AccountActions.getCurrentAccount, (state) => ({
    ...state,
    performanceData: {
      graphData: [],
      loading: false,
      filter: {
        range: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE,
        chartType: PerformanceType.PERFORMANCE,
        showPortfolioChanges: true,
        showBenchmark: false,
        lastExtendedRange: PerformanceFilterRange.LAST_PORTFOLIO_UPDATE
      } as PerformanceDataFilter
    }
  })),
  on(AccountActions.changeLanguage, (state) => ({ ...state, marketUpdates: { ...state.marketUpdates, loadState: 'INIT' as LoadState } })),
  // NgRx store clean up after logout
  on(AccountActions.customerLogout, () => ({ ...initialState })),
  on(AccountActions.customerLogoutAndUnpairDevice, () => ({ ...initialState }))
);

export function reducerPerformance(state: PerformanceState | undefined, action: Action) {
  return performanceReducer(state, action);
}

export const getPerformanceData = (state: PerformanceState) => state.performanceData;
export const getPerformanceDataFilter = (performanceData: PerformanceData) => performanceData.filter;
export const getPerformanceGraphData = (performanceData: PerformanceData) => performanceData.graphData;
export const getPerformanceDataLoading = (performanceData: PerformanceData) => performanceData.loading;
export const getMarketUpdates = (state: PerformanceState) => state.marketUpdates;
